import { QuoteStatus } from "src/api-service";

export interface TimelineNode {
    status: QuoteStatus;
    /** i18n key for the node label */
    labelKey: string;
    /** PrimeIcons class for the node icon */
    icon: string;
}

/** The canonical "happy path" through which a quote progresses. Review and
 *  drawing are modelled as detour lanes that arch above this row, not as
 *  inline steps — see DETOUR_KINDS below. */
export const MAIN_PATH: TimelineNode[] = [
    { status: QuoteStatus.Draft,    labelKey: "QUOTE_LIST.STATUS_LABEL.DRAFT",    icon: "pi-pencil" },
    { status: QuoteStatus.Sent,     labelKey: "QUOTE_LIST.STATUS_LABEL.SENT",     icon: "pi-send"   },
    { status: QuoteStatus.Approved, labelKey: "QUOTE_LIST.STATUS_LABEL.APPROVED", icon: "pi-check"  },
];

/** Drawing detour — the side loop. Source = preDrawingStatus, destination = same. */
export const DRAWING_DETOUR_PATH: TimelineNode[] = [
    { status: QuoteStatus.ReadyForDrawing,   labelKey: "QUOTE_LIST.STATUS_LABEL.READYFORDRAWING",   icon: "pi-pencil" },
    { status: QuoteStatus.DrawingInProgress, labelKey: "QUOTE_LIST.STATUS_LABEL.DRAWINGINPROGRESS", icon: "pi-clock"  },
];

/** Review detour — the side loop for sales review. Source = whatever main
 *  status preceded ReadyForReview (typically Sent), destination = same. */
export const REVIEW_DETOUR_PATH: TimelineNode[] = [
    { status: QuoteStatus.ReadyForReview, labelKey: "QUOTE_LIST.STATUS_LABEL.READYFORREVIEW", icon: "pi-eye"    },
    { status: QuoteStatus.UnderReview,    labelKey: "QUOTE_LIST.STATUS_LABEL.UNDERREVIEW",    icon: "pi-search" },
];

/** Statuses that terminate the flow — append a terminator node and hide path past current. */
export const TERMINAL_STATUSES = new Set<QuoteStatus>([
    QuoteStatus.Lost,
    QuoteStatus.Archived,
    QuoteStatus.AutoArchived,
    QuoteStatus.Removed,
    QuoteStatus.Replaced,
]);

export const TERMINATOR_LABELS: Record<number, string> = {
    [QuoteStatus.Lost]:         "QUOTE_LIST.STATUS_LABEL.LOST",
    [QuoteStatus.Archived]:     "QUOTE_LIST.STATUS_LABEL.ARCHIVED",
    [QuoteStatus.AutoArchived]: "QUOTE_LIST.STATUS_LABEL.AUTOARCHIVED",
    [QuoteStatus.Removed]:      "QUOTE_LIST.STATUS_LABEL.REMOVED",
    [QuoteStatus.Replaced]:     "QUOTE_LIST.STATUS_LABEL.REPLACED",
};

export const DRAWING_DETOUR_STATUSES = new Set<QuoteStatus>([
    QuoteStatus.ReadyForDrawing,
    QuoteStatus.DrawingInProgress,
]);

export const REVIEW_DETOUR_STATUSES = new Set<QuoteStatus>([
    QuoteStatus.ReadyForReview,
    QuoteStatus.UnderReview,
]);

export type DetourKind = "drawing" | "review";

/** Static lookup table for the two detour configurations. Keeps the episode
 *  computation in the component agnostic of which side loop it's tracking. */
export interface DetourKindConfig {
    kind: DetourKind;
    path: TimelineNode[];
    statuses: Set<QuoteStatus>;
    /** The "first" sub-status (the entry node from main). Used to detect when
     *  an episode transitioned past it to its second sub-status. */
    entryStatus: QuoteStatus;
    /** The "second" sub-status (the in-progress node). */
    progressStatus: QuoteStatus;
    /** Where the placeholder arch is anchored when the quote has never
     *  taken this detour. The arch spans one main column, so its left and
     *  right legs land on two adjacent main bubbles — for drawing this
     *  bridges Draft and Sent, visually surfacing both real entrances
     *  even though the data model only stores the anchor column. */
    canonicalSourceStatus: QuoteStatus;
}

export const DETOUR_KINDS: DetourKindConfig[] = [
    {
        kind:                  "review",
        path:                  REVIEW_DETOUR_PATH,
        statuses:              REVIEW_DETOUR_STATUSES,
        entryStatus:           QuoteStatus.ReadyForReview,
        progressStatus:        QuoteStatus.UnderReview,
        canonicalSourceStatus: QuoteStatus.Sent,
    },
    {
        kind:                  "drawing",
        path:                  DRAWING_DETOUR_PATH,
        statuses:              DRAWING_DETOUR_STATUSES,
        entryStatus:           QuoteStatus.ReadyForDrawing,
        progressStatus:        QuoteStatus.DrawingInProgress,
        // Drawing can be entered from Draft OR Sent. Anchoring at Draft
        // makes the arch span Draft → Sent so both legs land on the real
        // entrance bubbles; rendering a second arch from Sent would
        // duplicate the same detour, so we keep just this one.
        canonicalSourceStatus: QuoteStatus.Draft,
    },
];

export type NodeState = "completed" | "current" | "future";

export interface RenderedNode extends TimelineNode {
    state: NodeState;
    /** ISO date string for the moment the quote entered this status, if known. */
    date?: Date | null;
}

export type EpisodeState = "active" | "completed" | "future";

/** Which sub-status the quote currently sits at, for active episodes only.
 *  Used to drive per-leg arch illumination — the right leg of the frame
 *  should only light up after the quote has reached `progress`. */
export type EpisodeFocus = "entry" | "progress" | null;

/** A single detour "episode" — one trip through entry → progress and back.
 *  The quote may have several of these across its lifetime, anchored at
 *  different source statuses on the main path. State="future" is a
 *  placeholder for a detour the quote could still take but hasn't yet. */
export interface DetourEpisode {
    kind: DetourKind;
    sourceStatus: QuoteStatus;
    /** 0-based index into MAIN_PATH for grid positioning. */
    sourceIdx: number;
    state: EpisodeState;
    /** The two detour bubbles in their per-episode states. */
    nodes: RenderedNode[];
    /** 0-based vertical row used to stack arches that share a source column.
     *  Row 0 is closest to the main path; higher rows float above. */
    row: number;
    /** For active episodes: which sub-status the quote sits at right now.
     *  Null for completed/future episodes. Drives whether the right half of
     *  the arch is shown as visited or as still-pending. */
    at: EpisodeFocus;
}
